import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Building2, SearchX } from "lucide-react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { EmptyState } from "@/components/EmptyState";
import { ListingGridSkeleton } from "@/components/skeletons";
import { ListingCard } from "@/components/ListingCard";
import { AreaPicker, resolveArea } from "@/components/AreaPicker";
import { DateFilterPills, periodSince, type Period } from "@/components/DateFilterPills";

import { OTHER_AREA_VALUE } from "@/data/istanbulAreas";
import {
  apartmentTypeOptions,
  formatApartmentType,
  type ApartmentType,
  type ListingType,
  type PublicListing,
} from "@/lib/listings";
import { SEO } from "@/lib/seo";

const PAGE_SIZE = 12;
type SortKey = "newest" | "oldest" | "priceAsc" | "priceDesc";
const SORT_KEYS: SortKey[] = ["newest", "oldest", "priceAsc", "priceDesc"];

const AvailableListings = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<PublicListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  // Filters from URL (single source of truth)
  const type = (searchParams.get("type") as "all" | ListingType) || "all";
  const apt = (searchParams.get("apt") as "all" | ApartmentType) || "all";
  const areaValue = searchParams.get("area") || "";
  const areaOther = searchParams.get("areaOther") || "";
  const furnishedParam = searchParams.get("furnished");
  const furnished: "all" | "yes" | "no" | "semi" =
    furnishedParam === "yes" || furnishedParam === "no" || furnishedParam === "semi"
      ? furnishedParam
      : "all";
  const citizenshipOnly = searchParams.get("citizenship") === "true";
  const period = ((searchParams.get("period") as Period) || "all");
  const sort = ((SORT_KEYS as string[]).includes(searchParams.get("sort") ?? "")
    ? (searchParams.get("sort") as SortKey)
    : "newest");

  const updateParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (!v || v === "all" || (k === "sort" && v === "newest")) next.delete(k);
      else next.set(k, v);
    });
    setSearchParams(next, { replace: true });
  };

  const clearAll = () => setSearchParams({}, { replace: true });

  const hasActiveFilter =
    type !== "all" || apt !== "all" || !!areaValue || furnished !== "all" || citizenshipOnly || period !== "all" || sort !== "newest";

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      let q = supabase.from("public_listings").select("*, listing_images(*)");
      if (type !== "all") q = q.eq("listing_type", type);
      if (apt !== "all") q = q.eq("apartment_type", apt);
      const areaStr = resolveArea(areaValue, areaOther);
      if (areaStr) q = q.ilike("area", `%${areaStr}%`);
      if (furnished !== "all") q = q.eq("furnished", furnished);
      if (citizenshipOnly) q = q.eq("eligible_for_citizenship", true);
      const since = periodSince(period);
      if (since) q = q.gte("created_at", since.toISOString());
      if (sort === "oldest") q = q.order("created_at", { ascending: true });
      else if (sort === "priceAsc") q = q.order("price", { ascending: true, nullsFirst: false });
      else if (sort === "priceDesc") q = q.order("price", { ascending: false, nullsFirst: false });
      else q = q.order("created_at", { ascending: false });
      const { data } = await q;
      setItems((data ?? []) as PublicListing[]);
      setLoading(false);
      setPage(1);
    };
    void load();
  }, [type, apt, areaValue, areaOther, furnished, citizenshipOnly, period, sort]);

  const paged = useMemo(() => items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [items, page]);
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));

  return (
    <section className="min-h-screen bg-background px-4 py-10 sm:px-6 lg:px-8">
      <SEO title={t("seo.listings.title")} description={t("seo.listings.description")} path="/listings" />
      <div className="mx-auto max-w-7xl">
        <h1 className="text-3xl font-black">{t("listings.title")}</h1>
        <p className="mt-2 text-muted-foreground">{t("listings.subtitle")}</p>

        {/* FILTERS */}
        <div className="mt-6 space-y-3">
          {/* Type pills */}
          <div className="flex flex-wrap gap-2">
            {(["rent", "sale"] as const).map((value) => {
              const active = type === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => updateParams({ type: active ? null : value })}
                  aria-pressed={active}
                  className={`inline-flex min-h-[44px] items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm font-bold transition ${
                    active
                      ? "border-primary bg-primary text-primary-foreground shadow"
                      : "border-border bg-card text-foreground hover:border-primary"
                  }`}
                >
                  {value === "rent" ? t("listing.card.badge.rent") : t("listing.card.badge.sale")}
                </button>
              );
            })}
          </div>

          {/* Dropdowns + furnished */}
          <div className="grid gap-3 sm:grid-cols-2 md:flex md:flex-wrap md:items-center">
            <div className="min-w-0 md:w-56">
              <AreaPicker
                value={areaValue}
                otherText={areaOther}
                allowEmpty
                emptyLabel={t("listings.filter.areaAll")}
                placeholder={t("listings.filter.areaAll")}
                onChange={(v, ot) =>
                  updateParams({
                    area: v || null,
                    areaOther: v === OTHER_AREA_VALUE ? ot || null : null,
                  })
                }
              />
            </div>
            <select
              value={apt}
              onChange={(e) => updateParams({ apt: e.target.value })}
              className="min-h-[44px] rounded-md border border-border bg-background px-3 py-2 text-sm md:w-48"
              aria-label={t("listings.filter.apartmentType")}
            >
              <option value="all">{t("listings.filter.apartmentTypeAll")}</option>
              {apartmentTypeOptions.map((o) => (
                <option key={o} value={o}>
                  {formatApartmentType(o)}
                </option>
              ))}
            </select>
            <select
              value={furnished}
              onChange={(e) => updateParams({ furnished: e.target.value })}
              aria-label={t("listings.filter.furnishedLabel")}
              className="min-h-[44px] rounded-md border border-border bg-background px-3 py-2 text-sm md:w-48"
            >
              <option value="all">{t("listings.filter.furnishedLabel")} — {t("listings.filter.furnishedAll")}</option>
              <option value="yes">{t("furnished.yes")}</option>
              <option value="no">{t("furnished.no")}</option>
              <option value="semi">{t("furnished.semi")}</option>
            </select>
            {hasActiveFilter && (
              <button
                type="button"
                onClick={clearAll}
                className="min-h-[44px] rounded-full border border-border bg-card px-4 py-2 text-sm font-bold text-foreground hover:border-primary"
              >
                {t("listings.filter.clear")}
              </button>
            )}
          </div>

          {/* Date pills + Sort */}
          <div className="flex flex-wrap items-center gap-3">
            <DateFilterPills value={period} onChange={(p) => updateParams({ period: p === "all" ? null : p })} />
            <select
              value={sort}
              onChange={(e) => updateParams({ sort: e.target.value })}
              aria-label={t("listings.filters.sortLabel")}
              className="min-h-[44px] rounded-md border border-border bg-background px-3 py-2 text-sm md:ms-auto"
            >
              {SORT_KEYS.map((s) => (
                <option key={s} value={s}>
                  {t(`listings.filters.sort${s.charAt(0).toUpperCase()}${s.slice(1)}`)}
                </option>
              ))}
            </select>
          </div>

          {!loading && items.length > 0 && (
            <p className="text-sm text-muted-foreground">
              {t("listings.results.count", { count: items.length })}
            </p>
          )}
        </div>

        {/* GRID */}
        <div className="mt-8">
          {loading ? (
            <ListingGridSkeleton count={6} />
          ) : items.length === 0 ? (
            hasActiveFilter ? (
              <EmptyState
                icon={SearchX}
                title={t("listings.empty.title")}
                ctaLabel={t("listings.empty.cta")}
                ctaOnClick={clearAll}
              />
            ) : (
              <EmptyState
                icon={Building2}
                title={t("listings.emptyTitle")}
                message={t("listings.emptyText")}
                ctaLabel={t("home.ctaRequest")}
                ctaTo="/request"
              />
            )
          ) : (
            <>
              <div className="grid gap-3 animate-fade-in md:grid-cols-2 md:gap-4 xl:grid-cols-3">
                {paged.map((l) => (
                  <ListingCard key={l.id} listing={l} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-8 flex items-center justify-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-40"
                  >
                    {t("listings.prev")}
                  </button>
                  <span className="text-sm font-bold">
                    {t("listings.page")} {page} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-40"
                  >
                    {t("listings.next")}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default AvailableListings;
